import { Box, Autocomplete, TextField, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { useEffect } from "react";
import { normalizeForSearch } from "../utils/search";
import InputTerasu from "./InputTerasu";
import InputAbility from "./InputAbility";
import InputStatus from "./InputStatus";
import InputStats from "./InputStats";
import InputMoves from "./InputMoves";
import { LeftSideBarVM } from "./useViewModel";
import { POKEMON_ITEM_ID_NO_SET } from "../types/item";
import PokemonItemSelect from "../components/PokemonItemSelect";
import { filterSelectableItemsForPokemon } from "../lib/pokemon-config";

function LeftSidebar({ vm }: { vm: LeftSideBarVM }){
    const {
      selectedPokemon,
      setSelectedPokemon,
      pokemonOptions,
      handlePokemonSelect,
      statInputs,
      selectedDefenseItem,
      setSelectedDefenseItem,
      handleStatChange,
      handlePersonalityChange,
      createIncrementHandler,
      createDecrementHandler,
      totalEvs,
      handleSelectAbilityForDefensePokemon,
      getDefenseItems,
      handleIndividualValueChange,
      selectedMoves,
      availableMoves,
      onMoveSelect,
      getMoveDisplayName,
      getMoveDisplayPower,
      currentMode,
      setCurrentMode,
      isComprehensiveMode,
      setIsComprehensiveMode,
    } = vm;
    // フォーム固定アイテムの自動設定ロジックを lib に委譲
    useEffect(() => {
        if (!selectedPokemon) return;
        // 選出されたポケモンに応じて自動付与すべきアイテムがあるか確認する
        // lib/pokemon-config のルールに従って付与する
        import('../lib/pokemon-config').then(({ getAutoItemForPokemon, canPokemonHoldItem }) => {
            const autoItem = getAutoItemForPokemon(selectedPokemon);
            if (autoItem != null) {
                setSelectedDefenseItem(autoItem);
                // 左サイドバー内の選択状態も同期させる（UIと内部状態の不整合を防ぐため）
                setSelectedPokemon(prev => prev ? {
                    ...prev,
                    selected_item_id: autoItem
                } : null);
                return;
            }

            // ゲノセクト以外に持ち越されたカセット系は解除する（手動で選べないようにするため）
            if (!canPokemonHoldItem(selectedPokemon.id, selectedDefenseItem)) {
              setSelectedDefenseItem(POKEMON_ITEM_ID_NO_SET);
              setSelectedPokemon((prev) =>
                prev
                  ? {
                      ...prev,
                      selected_item_id: POKEMON_ITEM_ID_NO_SET,
                    }
                  : null,
              );
              return;
            }

            // メガストーンは手動選択できないため、メガ以外に持ち越された場合は解除する
            import('../lib/mega-stone').then(({ isMegaStoneItemId }) => {
              if (isMegaStoneItemId(selectedDefenseItem)) {
                setSelectedDefenseItem(POKEMON_ITEM_ID_NO_SET);
                setSelectedPokemon((prev) =>
                  prev
                    ? {
                        ...prev,
                        selected_item_id: POKEMON_ITEM_ID_NO_SET,
                      }
                    : null,
                );
              }
            });
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPokemon?.id, setSelectedDefenseItem, setSelectedPokemon]);

    return <>
      <Box sx={{ 
        width: '400px', 
        borderRight: { md: '1px solid #e0e0e0' },
        p: 2,
        overflowY: 'scroll',
        overflowX: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: '#f1f1f1',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#c1c1c1',
          borderRadius: '4px',
          '&:hover': {
            backgroundColor: '#a8a8a8',
          },
        },
      }}>
        {/* 一時的に非表示。総合/レガシー切替のロジックは残すため削除しない。 */}
        {false && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <ToggleButtonGroup
              value={isComprehensiveMode ? 'comprehensive' : 'legacy'}
              exclusive
              onChange={(_, nextMode) => {
                if (!nextMode) return;
                setIsComprehensiveMode(nextMode === 'comprehensive');
              }}
              size="small"
            >
              <ToggleButton value="comprehensive" sx={{ px: 2 }}>
                総合
              </ToggleButton>
              <ToggleButton value="legacy" sx={{ px: 2 }}>
                レガシー
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}

        {/* 一時的に非表示。防御/攻撃/素早さ切替のロジックは残すため削除しない。 */}
        {false && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <ToggleButtonGroup
              value={currentMode}
              exclusive
              onChange={(_, newMode) => {
                if (newMode !== null) {
                  setCurrentMode(newMode);
                }
              }}
              size="small"
              sx={{ marginLeft: 'auto' }}
            >
              <ToggleButton value="defense" sx={{ px: 2 }}>
                防御
              </ToggleButton>
              <ToggleButton value="attack" sx={{ px: 2 }}>
                攻撃
              </ToggleButton>
              <ToggleButton value="speed" sx={{ px: 2 }}>
                素早さ
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}
        
        {/* Pokemon Search and Selection */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
          {/* クリアボタンを表示しないようにして誤操作を防ぐ */}
          <Autocomplete
            disableClearable
            options={pokemonOptions}
            getOptionLabel={(option) => option.label}
            value={selectedPokemon ? pokemonOptions.find(option => option.pokemon.id === selectedPokemon.id) || undefined : undefined}
            onChange={handlePokemonSelect}
            filterOptions={(options, { inputValue }) => {
              if (!inputValue) return options;
              
              const normalizedInput = normalizeForSearch(inputValue);
              return options.filter(option => 
                normalizeForSearch(option.label).includes(normalizedInput)
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="ポケモンを検索"
                placeholder="ポケモンを選択"
                variant="outlined"
                fullWidth
              />
            )}
            sx={{ flex: 1 }}
          />
          {selectedPokemon && selectedPokemon.picture_url && (
            <Box
              component="img"
              src={selectedPokemon.picture_url}
              alt={selectedPokemon.name_ja}
              sx={{
                width: 64,
                height: 64,
                transform: 'scaleX(-1)',
                imageRendering: 'pixelated',
                flexShrink: 0
              }}
            />
          )}
        </Box>

        {selectedPokemon && (
          <>
            <Box sx={{ display: 'flex', gap: 3, mt: 3 }}>
              <InputTerasu
                selectedPokemon={selectedPokemon}
                setSelectedPokemon={setSelectedPokemon}
              />
              
              <InputAbility
                selectedPokemon={selectedPokemon}
                handleSelectAbilityForDefensePokemon={handleSelectAbilityForDefensePokemon}
              />
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, mt: 2, flexWrap: 'nowrap' }}>
              <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
                <PokemonItemSelect
                  pokemon={selectedPokemon}
                  items={filterSelectableItemsForPokemon(selectedPokemon.id, getDefenseItems)}
                  value={selectedDefenseItem}
                  onChange={(itemId) => {
                    setSelectedDefenseItem(itemId);
                    // Update the selected Pokemon's defense item as well
                    if (selectedPokemon) {
                      setSelectedPokemon((prev) =>
                        prev
                          ? {
                              ...prev,
                              selected_item_id: itemId,
                            }
                          : null,
                      );
                    }
                  }}
                  variant="sidebar"
                />
              </Box>
              <Box sx={{ flex: '0 0 120px' }}>
                <InputStatus
                  selectedPokemon={selectedPokemon}
                  setSelectedPokemon={setSelectedPokemon}
                />
              </Box>
            </Box>
            
            <Box sx={{ mb: 0 }}>
              <InputStats
                selectedPokemon={selectedPokemon}
                setSelectedPokemon={setSelectedPokemon}
                statInputs={statInputs}
                handleStatChange={handleStatChange}
                handlePersonalityChange={handlePersonalityChange}
                createIncrementHandler={createIncrementHandler}
                createDecrementHandler={createDecrementHandler}
                totalEvs={totalEvs}
                handleIndividualValueChange={handleIndividualValueChange}
              />
            </Box>
            
            <InputMoves
              selectedPokemon={selectedPokemon}
              selectedMoves={selectedMoves}
              availableMoves={availableMoves}
              onMoveSelect={onMoveSelect}
              getMoveDisplayName={getMoveDisplayName}
              getMoveDisplayPower={getMoveDisplayPower}
            />
          </>
        )}
      </Box>
    
    </>
}
export default LeftSidebar;
